'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { FileText, CheckCircle, AlertCircle, Scale } from 'lucide-react';

export default function TermsOfServicePage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-slate-950 to-slate-900">
      <Navbar />

      <main className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-10 text-center">
            <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-blue-600/20 mb-4">
              <Scale className="h-8 w-8 text-blue-500" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-lg text-slate-300">Guidelines for using SlashML AI's services</p>
          </motion.div>

          <motion.div
            className="space-y-8 bg-slate-800/50 backdrop-blur-sm rounded-xl p-8 border border-slate-700 shadow-xl"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}>
            <motion.div variants={fadeIn}>
              <div className="flex items-start mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-4">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-white">Service Agreement</h2>
              </div>
              <p className="text-slate-300 pl-11">
                By accessing or using our service, you agree to be bound by these Terms. We provide an AI-powered data
                analysis platform that allows you to interact with your data through natural language queries and
                receive visualizations and insights.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-start mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-4">
                  <CheckCircle className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-white">Account Responsibilities</h2>
              </div>
              <p className="text-slate-300 pl-11">
                You are responsible for maintaining the confidentiality of your account credentials and for all
                activities that occur under your account. You agree to notify us immediately of any unauthorized access
                or use of your account.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-start mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-4">
                  <AlertCircle className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-white">Limitations and Restrictions</h2>
              </div>
              <p className="text-slate-300 pl-11">
                You may not use our service to upload, transmit, or share content that is illegal, harmful, or violates
                the rights of others. We reserve the right to remove any content and terminate accounts that violate
                these Terms without prior notice.
              </p>
            </motion.div>

            <motion.div variants={fadeIn}>
              <div className="flex items-start mb-2">
                <div className="bg-blue-600/20 p-2 rounded-lg mr-4">
                  <Scale className="h-5 w-5 text-blue-500" />
                </div>
                <h2 className="text-xl font-semibold text-white">Intellectual Property</h2>
              </div>
              <p className="text-slate-300 pl-11">
                Our service and its contents are protected by copyright, trademark, and other laws. You retain ownership
                of your data, but grant us a limited license to use it to provide and improve our services. We own all
                intellectual property rights to our platform and technology.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="mt-8 p-6 bg-blue-600/10 rounded-xl border border-blue-600/20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}>
            <h3 className="text-lg font-semibold text-white mb-2">Subscription and Billing</h3>
            <p className="text-slate-300">
              We offer different subscription plans with varying features and usage limits. By subscribing to a paid
              plan, you agree to pay all fees in accordance with the billing terms. Prices are subject to change, but we
              will notify you before implementing any changes to your current subscription.
            </p>
          </motion.div>

          <motion.div
            className="mt-10 text-center text-slate-400 text-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.5 }}>
            <p>Last updated: February 15, 2025</p>
            <p className="mt-2">
              By using our services, you acknowledge that you have read and understand these Terms. If you have any
              questions, please{' '}
              <a href="mailto:support@slashml.com" className="text-blue-400 hover:text-blue-300">
                contact us
              </a>
              .
            </p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
